import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { CatalogHeader, errorText } from "./common";

interface Message {
  kind: "info" | "error";
  text: string;
}

const where = (d: { state: string; zipPrefix: string | null }) => (d.zipPrefix ? `${d.state} ZIP ${d.zipPrefix}xx` : `${d.state} (whole state)`);

export function RegionDefaultsPage() {
  const api = useApi();
  const defaults = useLoad((a) => a.admin.catalog.regionDefaults());
  const panels = useLoad((a) => a.admin.catalog.panels());
  const profiles = useLoad((a) => a.admin.catalog.profiles());
  const [form, setForm] = useState({ state: "", zipPrefix: "", trayPanelId: "", allergenProfileId: "" });
  const [probe, setProbe] = useState({ state: "", zip: "" });
  const [resolved, setResolved] = useState<Message | null>(null);
  const [msg, setMsg] = useState<Message | null>(null);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const zipPrefix = form.zipPrefix.trim();
  const formReady = form.state.length === 2 && (zipPrefix === "" || /^\d{3}$/.test(zipPrefix)) && form.trayPanelId !== "" && form.allergenProfileId !== "";

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !formReady) return;
    setBusy(true);
    setMsg(null);
    try {
      const d = await api.admin.catalog.upsertRegionDefault({ state: form.state, zipPrefix: zipPrefix || null, trayPanelId: form.trayPanelId, allergenProfileId: form.allergenProfileId });
      setMsg({ kind: "info", text: `Saved the default for ${where(d)}.` });
      defaults.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function remove(d: { id: string; state: string; zipPrefix: string | null }) {
    if (busy) return;
    if (!window.confirm(`Remove the default for ${where(d)}? Locations keep the panel and profile already set on them.`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.admin.catalog.deleteRegionDefault(d.id);
      setMsg({ kind: "info", text: `Removed the default for ${where(d)}.` });
      defaults.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  /** An answer belongs to the question that was asked, so changing the question clears it. */
  function changeProbe(patch: Partial<typeof probe>) {
    setProbe({ ...probe, ...patch });
    setResolved(null);
  }
  async function resolve(e: React.FormEvent) {
    e.preventDefault();
    if (looking) return;
    setLooking(true);
    setResolved(null);
    const zip = probe.zip.trim();
    const asked = `${probe.state}${zip ? ` ${zip}` : ""}`;
    try {
      const r = await api.admin.catalog.resolveRegionDefault(probe.state, zip || undefined);
      setResolved({
        kind: "info",
        text: r.default
          ? `${asked}: ${r.default.trayPanelCode} tray, ${r.default.allergenProfileCode} profile (matched by ${r.matchedBy === "zip_prefix" ? "ZIP prefix" : "state"}).`
          : `${asked}: no default for that state yet.`,
      });
    } catch (err) {
      setResolved({ kind: "error", text: errorText(err) });
    } finally {
      setLooking(false);
    }
  }

  const choose = (loading: boolean, empty: string) => [{ value: "", label: loading ? "Loading…" : empty }];
  const panelOptions = [...choose(panels.state === "loading", "Choose a panel"), ...(panels.data?.families ?? []).map((f) => ({ value: f.id, label: `${f.code} · ${f.name}` }))];
  const profileOptions = [...choose(profiles.state === "loading", "Choose a profile"), ...(profiles.data?.families ?? []).map((f) => ({ value: f.id, label: `${f.code} · ${f.name}` }))];
  return (
    <>
      <CatalogHeader title="Region defaults" intro="The panel and profile a new location will start with: by state, or by the first three digits of a ZIP code within a state. The most specific default wins; staff can still choose per location. Defaults will pre-fill new locations once locations carry addresses; until then, set each location's panel and profile under Organizations → Locations." />
      {defaults.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load region defaults (${errorText(defaults.error)})`} /></div>}
      {panels.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load panels (${errorText(panels.error)})`} /></div>}
      {profiles.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load profiles (${errorText(profiles.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <Table caption={defaults.data ? `${defaults.data.defaults.length} defaults` : defaults.state === "error" ? "Not loaded" : "Loading…"}>
            <thead>
              <tr>
                <Th>State</Th>
                <Th>ZIP prefix</Th>
                <Th>Panel</Th>
                <Th>Profile</Th>
                <Th><span className="sr-only">Actions</span></Th>
              </tr>
            </thead>
            <tbody>
              {defaults.data?.defaults.map((d) => (
                <tr key={d.id}>
                  <Td>{d.state}</Td>
                  <Td>{d.zipPrefix ? `${d.zipPrefix}xx` : "whole state"}</Td>
                  <Td>{d.trayPanelCode}</Td>
                  <Td>{d.allergenProfileCode}</Td>
                  <Td><Button variant="secondary" surface="card" disabled={busy} aria-label={`Remove the default for ${where(d)}`} onClick={() => void remove(d)}>Remove</Button></Td>
                </tr>
              ))}
            </tbody>
          </Table>
          {defaults.data?.defaults.length === 0 && <p className="mt-3 text-ink-secondary">No region defaults yet. Set the first one.</p>}
        </Card>
        <div className="grid content-start gap-6">
          <Card as="section">
            <h2 className="mb-4 text-h3 font-semibold text-anchor">Set a default</h2>
            <form onSubmit={(e) => void save(e)} className="grid gap-4">
              <Field label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase() })} maxLength={2} required hint="Two letters, e.g. FL" />
              <Field
                label="ZIP prefix (optional)"
                value={form.zipPrefix}
                onChange={(e) => setForm({ ...form, zipPrefix: e.target.value.replace(/\D/g, "") })}
                maxLength={3}
                inputMode="numeric"
                hint="Exactly three digits, the start of a ZIP code in this state; leave blank for the whole state"
              />
              <Select label="Tray panel" value={form.trayPanelId} onChange={(e) => setForm({ ...form, trayPanelId: e.target.value })} options={panelOptions} />
              <Select label="Allergen profile" value={form.allergenProfileId} onChange={(e) => setForm({ ...form, allergenProfileId: e.target.value })} options={profileOptions} />
              <div><Button type="submit" disabled={busy || !formReady}>Save default</Button></div>
            </form>
          </Card>
          <Card as="section">
            <h2 className="mb-4 text-h3 font-semibold text-anchor">Which default applies?</h2>
            <form onSubmit={(e) => void resolve(e)} className="grid gap-4">
              <Field label="State" value={probe.state} onChange={(e) => changeProbe({ state: e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase() })} maxLength={2} required hint="Two letters" />
              <Field label="ZIP code (optional)" value={probe.zip} onChange={(e) => changeProbe({ zip: e.target.value })} maxLength={10} inputMode="numeric" />
              <div><Button variant="secondary" type="submit" disabled={looking || probe.state.length !== 2}>Look up</Button></div>
              {resolved && (resolved.kind === "error" ? <Status kind="error" label={resolved.text} /> : <p role="status">{resolved.text}</p>)}
            </form>
          </Card>
        </div>
      </div>
    </>
  );
}
